import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import TabletLayout from "./TabletLayout";
import mesApi from "../../../Api/mes.api";
import { TimelineBatch } from "../../../types/mes";
import { toast, ToastContainer } from "react-toastify";
import { formatClockTime } from "../../../utils/processParameterSerialize";
import { t } from "../../../i18n/locale";

/**
 * Tablet Processing View - Shows batches in processing phase
 * Filters by proc_name (processing machine)
 */
const TabletProcessing = () => {
  const { machine } = useParams<{ machine?: string }>();
  const machineName = machine || "Processing";

  const [batches, setBatches] = useState<TimelineBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    loadBatches();
    const refreshInterval = setInterval(loadBatches, 30000);
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);

    return () => {
      clearInterval(refreshInterval);
      clearInterval(clockInterval);
    };
  }, [machineName]);

  const loadBatches = async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      // Production-day view: everything physically processed today, incl. inbound
      // semi-finished made today for other days' plans.
      const { batches: dayBatches } = await mesApi.getProductionDay(today);

      if (dayBatches.length) {
        const processingBatches = dayBatches
          .filter((b: TimelineBatch) => b.ProcessName === machineName || !machine)
          .sort((a: TimelineBatch, b: TimelineBatch) => {
            const aTime = a.ProcessingStart ? new Date(a.ProcessingStart).getTime() : 0;
            const bTime = b.ProcessingStart ? new Date(b.ProcessingStart).getTime() : 0;
            return aTime - bTime;
          });

        setBatches(processingBatches);
      } else {
        setBatches([]);
      }
    } catch (error: any) {
      toast.error(t("Greška: {message}", { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr?: string) => formatClockTime(dateStr);

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      planned: { label: "PLANIRANO", color: "#6c757d" },
      in_progress: { label: "U OBRADI", color: "#28a745" },
      completed: { label: "GOTOVO", color: "#007bff" },
    };
    const statusInfo = statusMap[status] || { label: status.toUpperCase(), color: "#6c757d" };
    return <span className="status-badge" style={{ background: statusInfo.color }}>{t(statusInfo.label)}</span>;
  };

  const isCurrentBatch = (batch: TimelineBatch): boolean => {
    if (!batch.ProcessingStart || !batch.ProcessingEnd) return false;
    const now = currentTime.getTime();
    const start = new Date(batch.ProcessingStart).getTime();
    const end = new Date(batch.ProcessingEnd).getTime();
    return now >= start && now <= end;
  };

  return (
    <TabletLayout station={machineName}>
      <ToastContainer position="top-center" autoClose={3000} />
      {loading ? (
        <div className="loading-state"><p>{t("Učitavanje...")}</p></div>
      ) : batches.length === 0 ? (
        <div className="empty-state"><p>{t("Nema planiranih obrada")}</p></div>
      ) : (
        <div className="batches-list">
          {batches.map((batch) => {
            const isCurrent = isCurrentBatch(batch);
            return (
              <div key={batch.ID} className={`batch-card ${isCurrent ? "current-batch" : ""}`}>
                <div className="batch-header">
                  <div className="batch-time">{formatTime(batch.ProcessingStart)}</div>
                  {getStatusBadge(batch.BatchStatus)}
                </div>
                <div className="batch-id">{batch.MixCode}</div>
                <div className="batch-details">
                  <div className="detail-item">
                    <span className="detail-label">{t("Proizvod:")}</span>
                    <span className="detail-value">{batch.ProductName || "-"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t("Masa:")}</span>
                    <span className="detail-value">{batch.FinalMassKg?.toFixed(1)} kg</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t("Trajanje:")}</span>
                    <span className="detail-value">~{batch.ProcessingTimeMin} min</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t("Završetak:")}</span>
                    <span className="detail-value">{formatTime(batch.ProcessingEnd)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <style>{`
        .batches-list {display: flex; flex-direction: column; gap: 2rem;}
        .batch-card {background: #2a2f3e; border-radius: 12px; padding: 2rem; border-left: 6px solid #6c757d; box-shadow: 0 4px 12px rgba(0,0,0,0.3); transition: all 0.3s;}
        .batch-card.current-batch {border-left-color: #28a745; background: #2e3d2f; box-shadow: 0 6px 20px rgba(40,167,69,0.3); animation: pulse 2s ease-in-out infinite;}
        @keyframes pulse {0%, 100% {box-shadow: 0 6px 20px rgba(40,167,69,0.3);} 50% {box-shadow: 0 8px 30px rgba(40,167,69,0.5);}}
        .batch-header {display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;}
        .batch-time {font-size: 3rem; font-weight: 700; color: #007bff; font-family: 'Courier New', monospace;}
        .status-badge {padding: 0.75rem 1.5rem; border-radius: 8px; font-size: 1.4rem; font-weight: 700; color: white; letter-spacing: 0.5px;}
        .batch-id {font-size: 2rem; font-weight: 600; color: #ffffff; margin-bottom: 1.5rem; word-break: break-word;}
        .batch-details {display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1.5rem;}
        .detail-item {display: flex; flex-direction: column; gap: 0.5rem;}
        .detail-label {font-size: 1.1rem; color: #adb3bd; text-transform: uppercase; letter-spacing: 0.5px;}
        .detail-value {font-size: 1.8rem; font-weight: 600; color: #ffffff;}
        .loading-state, .empty-state {display: flex; justify-content: center; align-items: center; height: 60vh; font-size: 2rem; color: #adb3bd;}
      `}</style>
    </TabletLayout>
  );
};

export default TabletProcessing;
